import { useState, useEffect } from "react";
import { supabase, Review } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";
import { Link } from "react-router-dom";

interface Props {
  providerId: string;
  totalRating: number;
  reviewCount: number;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={`text-sm ${s <= rating ? "text-gold" : "text-stone-200"}`}>★</span>
      ))}
    </div>
  );
}

function StarInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          onMouseEnter={() => setHovered(s)}
          onMouseLeave={() => setHovered(0)}
          className="text-2xl cursor-pointer transition-transform hover:scale-110"
        >
          <span className={s <= (hovered || value) ? "text-gold" : "text-stone-200"}>★</span>
        </button>
      ))}
    </div>
  );
}

export default function ProviderReviewSection({ providerId, totalRating, reviewCount }: Props) {
  const { currentUser, isLoggedIn, openReport } = useApp();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Review form state
  const [formRating, setFormRating] = useState(5);
  const [formContent, setFormContent] = useState("");
  const [formRole, setFormRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const fetchReviews = async () => {
    const { data } = await supabase
      .from("reviews")
      .select("*, reviewer:profiles!reviewer_id(id, name, avatar_url)")
      .eq("provider_id", providerId)
      .eq("is_hidden", false)
      .order("created_at", { ascending: false });
    setReviews((data as Review[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchReviews(); }, [providerId]);

  useEffect(() => {
    if (!currentUser) return;
    // Check if user purchased from this provider
    supabase
      .from("purchases")
      .select("id", { count: "exact", head: true })
      .eq("buyer_id", currentUser.id)
      .eq("provider_id", providerId)
      .eq("status", "completed")
      .then(({ count }) => setHasPurchased((count ?? 0) > 0));

    // Check if user already reviewed
    supabase
      .from("reviews")
      .select("id", { count: "exact", head: true })
      .eq("reviewer_id", currentUser.id)
      .eq("provider_id", providerId)
      .then(({ count }) => setHasReviewed((count ?? 0) > 0));
  }, [currentUser, providerId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !formRating) return;
    setSubmitting(true);
    setSubmitError("");

    // Get purchased product title for context
    const { data: purchase } = await supabase
      .from("purchases")
      .select("product_title")
      .eq("buyer_id", currentUser.id)
      .eq("provider_id", providerId)
      .eq("status", "completed")
      .order("purchased_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const { error } = await supabase.from("reviews").insert({
      reviewer_id: currentUser.id,
      provider_id: providerId,
      rating: formRating,
      content: formContent.trim() || null,
      reviewer_role: formRole.trim() || null,
      product_title: purchase?.product_title || null,
    });

    if (error) {
      setSubmitError("投稿に失敗しました。もう一度お試しください。");
    } else {
      setSubmitSuccess(true);
      setHasReviewed(true);
      setShowForm(false);
      setFormContent("");
      setFormRole("");
      setFormRating(5);
      await fetchReviews();
    }
    setSubmitting(false);
  };

  const visible = showAll ? reviews : reviews.slice(0, 3);

  const breakdown = [5, 4, 3, 2, 1].map((stars) => {
    const count = reviews.filter((r) => r.rating === stars).length;
    const pct = reviews.length > 0 ? Math.round((count / reviews.length) * 100) : 0;
    return { stars, count, pct };
  });

  const avgRating = reviews.length > 0
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : totalRating;

  return (
    <section id="reviews" className="py-14 px-4 md:px-0 border-t border-stone-100">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-gold text-xs font-display italic tracking-widest mb-1">Reviews</p>
          <h2 className="font-serif text-deep-green text-2xl font-light">受講者の声</h2>
        </div>
        {/* Review CTA */}
        {isLoggedIn && hasPurchased && !hasReviewed && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-gold text-white px-5 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-amber-500 transition-colors whitespace-nowrap"
          >
            <i className="ri-star-line" />レビューを書く
          </button>
        )}
        {!isLoggedIn && (
          <Link to="/login" className="flex items-center gap-2 border border-gold text-gold px-5 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-gold hover:text-white transition-colors whitespace-nowrap">
            <i className="ri-star-line" />ログインしてレビュー
          </Link>
        )}
        {isLoggedIn && !hasPurchased && (
          <p className="text-xs text-charcoal/40 font-sans flex items-center gap-1.5">
            <i className="ri-information-line" />購入済みの方がレビューを投稿できます
          </p>
        )}
      </div>

      {/* Review Form */}
      {showForm && (
        <div className="mb-8 bg-white rounded-2xl border border-gold/20 p-6">
          <h3 className="font-serif text-deep-green text-lg mb-4 flex items-center gap-2">
            <i className="ri-star-line text-gold" />レビューを投稿する
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-2">評価 <span className="text-red-400">*</span></label>
              <StarInput value={formRating} onChange={setFormRating} />
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1">あなたの立場・職業（任意）</label>
              <input
                value={formRole}
                onChange={e => setFormRole(e.target.value)}
                placeholder="例：着物愛好家、クリーニング業経営者、独立開業準備中"
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1">レビュー本文</label>
              <textarea
                value={formContent}
                onChange={e => setFormContent(e.target.value)}
                maxLength={500}
                rows={4}
                placeholder="講座・教材の感想、学んだこと、役立った点など（500文字以内）"
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none transition-colors"
              />
              <p className="text-right text-xs text-charcoal/30 font-sans mt-0.5">{formContent.length}/500</p>
            </div>
            {submitError && (
              <p className="text-xs text-red-500 font-sans">{submitError}</p>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="flex-1 py-3 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 cursor-pointer hover:bg-stone-50 whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={submitting || formRating === 0}
                className="flex-1 py-3 rounded-full bg-gold text-white text-sm font-sans cursor-pointer hover:bg-amber-500 transition-colors disabled:opacity-60 whitespace-nowrap flex items-center justify-center gap-2"
              >
                {submitting ? <><i className="ri-loader-4-line animate-spin" />投稿中...</> : "レビューを投稿する"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Submit success notice */}
      {submitSuccess && (
        <div className="mb-6 p-4 bg-teal/10 rounded-2xl border border-teal/20 flex items-center gap-3">
          <i className="ri-checkbox-circle-fill text-teal text-xl flex-shrink-0" />
          <p className="text-sm font-sans text-teal">レビューを投稿しました！ありがとうございます。</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-28 bg-stone-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 text-charcoal/40 font-sans bg-white/60 rounded-2xl border border-stone-100">
          <i className="ri-star-line text-4xl block mb-3 opacity-30" />
          <p className="text-sm">まだレビューはありません。</p>
          {hasPurchased && !hasReviewed && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 inline-flex items-center gap-2 bg-gold text-white px-5 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-amber-500 transition-colors"
            >
              <i className="ri-star-line" />最初のレビューを投稿する
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 mb-10">
          {/* Rating summary */}
          <div className="bg-ivory rounded-2xl p-6">
            <div className="text-center mb-4">
              <p className="font-display text-5xl text-deep-green font-light">{avgRating.toFixed(1)}</p>
              <div className="flex justify-center mt-1 mb-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <span key={s} className={`text-lg ${s <= Math.round(avgRating) ? "text-gold" : "text-stone-200"}`}>★</span>
                ))}
              </div>
              <p className="text-charcoal/40 text-xs font-sans">{reviews.length}件のレビュー</p>
            </div>
            <div className="flex flex-col gap-2">
              {breakdown.map((r) => (
                <div key={r.stars} className="flex items-center gap-2">
                  <span className="text-xs text-charcoal/50 font-sans w-3">{r.stars}</span>
                  <span className="text-gold text-xs">★</span>
                  <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                    <div className="h-full bg-gold rounded-full transition-all duration-500" style={{ width: `${r.pct}%` }} />
                  </div>
                  <span className="text-xs text-charcoal/40 font-sans w-7">{r.count}</span>
                </div>
              ))}
            </div>
            {hasPurchased && !hasReviewed && (
              <button
                onClick={() => setShowForm(true)}
                className="mt-4 w-full py-2.5 rounded-full bg-gold text-white text-xs font-sans cursor-pointer hover:bg-amber-500 transition-colors whitespace-nowrap"
              >
                レビューを書く
              </button>
            )}
          </div>

          {/* Review list */}
          <div className="lg:col-span-3 flex flex-col gap-5">
            {visible.map((review) => {
              const reviewer = review.reviewer as { name?: string; avatar_url?: string } | undefined;
              const avatarSrc = reviewer?.avatar_url ||
                `https://public.readdy.ai/ai/img_res/14cf68bb3752c5ade6ce244ed04c0c3b.jpg${review.id.slice(0, 8)}&orientation=squarish`;
              return (
                <div key={review.id} className="bg-white rounded-2xl p-5 border border-stone-100">
                  <div className="flex items-start gap-3 mb-3">
                    <img src={avatarSrc} alt={reviewer?.name ?? "受講者"} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <p className="font-sans text-sm font-medium text-charcoal">{reviewer?.name ?? "受講者"}</p>
                          {review.reviewer_role && <p className="text-xs text-charcoal/40 font-sans">{review.reviewer_role}</p>}
                        </div>
                        <span className="text-xs text-charcoal/30 font-sans whitespace-nowrap">
                          {new Date(review.created_at).toLocaleDateString("ja-JP", { year: "numeric", month: "long" })}
                        </span>
                      </div>
                      <div className="mt-1.5 mb-1"><StarRating rating={review.rating} /></div>
                      {review.product_title && <p className="text-xs text-charcoal/40 font-sans">講座：{review.product_title}</p>}
                    </div>
                  </div>
                  {review.content && <p className="text-sm text-charcoal/70 font-sans leading-relaxed">{review.content}</p>}
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={() => openReport({ type: "review", id: review.id, title: `レビュー by ${reviewer?.name ?? "受講者"}` })}
                      className="text-xs text-charcoal/20 hover:text-red-400 transition-colors cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-flag-line" />通報
                    </button>
                  </div>
                </div>
              );
            })}
            {!showAll && reviews.length > 3 && (
              <button onClick={() => setShowAll(true)} className="w-full py-3 rounded-xl border border-stone-200 text-sm text-charcoal/60 font-sans hover:border-teal hover:text-teal transition-colors cursor-pointer">
                すべてのレビューを見る（{reviews.length}件）
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
